import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Edit3, Save, Trash2, Clock, Plus, Play } from 'lucide-react';

import { Chapter as GeminiChapter } from '../services/geminiService';

interface Note {
  id: string;
  timestamp: string;
  title: string;
  content: string;
  chapterId?: string;
  createdAt: string;
}

interface NotesEditorProps {
  videoId: string;
  notes: Note[];
  onAddNote: (note: Omit<Note, 'id' | 'createdAt'>) => void;
  onDeleteNote: (noteId: string) => void;
  onJumpToTime: (time: number) => void;
  chapters: GeminiChapter[];
}

export function NotesEditor({ 
  videoId, 
  notes, 
  onAddNote, 
  onDeleteNote, 
  onJumpToTime,
  chapters 
}: NotesEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [newNote, setNewNote] = useState({
    timestamp: '0',
    title: '',
    content: '',
    chapterId: ''
  });

  const formatTime = (value: string | number) => {
    const seconds = typeof value === 'number' ? value : parseInt(value, 10) || 0;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSaveNote = () => {
    if (newNote.title.trim() && newNote.content.trim()) {
      onAddNote({
        timestamp: newNote.timestamp,
        title: newNote.title.trim(),
        content: newNote.content.trim(),
        chapterId: newNote.chapterId || undefined
      });
      setNewNote({ timestamp: '0', title: '', content: '', chapterId: '' });
      setIsEditing(false);
    }
  };

  const handleChapterSelect = (chapterId: string) => {
    const chapter = chapters.find(ch => ch.id === chapterId);
    if (chapter) {
      setNewNote(prev => ({
        ...prev,
        chapterId: chapterId,
        timestamp: chapter.startTime.toString()
      }));
    }
  };

  const getChapterTitle = (chapterId?: string) => {
    if (!chapterId) return null;
    const chapter = chapters.find(ch => ch.id === chapterId);
    return chapter?.title;
  };

  const sortedNotes = [...notes].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  return (
    <div className="space-y-4">
      {/* Add New Note */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="flex items-center gap-2">
            <Edit3 className="w-4 h-4" />
            학습 노트
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditing(!isEditing)}
          >
            <Plus className="w-4 h-4 mr-1" />
            새 노트
          </Button>
        </div>

        {isEditing && (
          <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
            <div className="space-y-2">
              <Label htmlFor="note-chapter">챕터 선택 (선택사항)</Label>
              <Select value={newNote.chapterId} onValueChange={handleChapterSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="챕터를 선택하세요" />
                </SelectTrigger>
                <SelectContent>
                  {chapters.map((chapter) => (
                    <SelectItem key={chapter.id} value={chapter.id}>
                      {chapter.title} ({formatTime(chapter.startTime)} - {formatTime(chapter.endTime)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="note-timestamp">타임스탬프 (초)</Label>
                <Input
                  id="note-timestamp"
                  type="text"
                  placeholder="0"
                  value={newNote.timestamp}
                  onChange={(e) => setNewNote({...newNote, timestamp: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="note-title">제목</Label>
                <Input
                  id="note-title"
                  placeholder="노트 제목을 입력하세요"
                  value={newNote.title}
                  onChange={(e) => setNewNote({...newNote, title: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="note-content">내용</Label>
              <Textarea
                id="note-content"
                placeholder="학습 내용이나 중요한 포인트를 기록하세요..."
                value={newNote.content}
                onChange={(e) => setNewNote({...newNote, content: e.target.value})}
                rows={4}
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSaveNote} size="sm">
                <Save className="w-4 h-4 mr-1" />
                저장
              </Button>
              <Button 
                variant="outline" 
                onClick={() => {
                  setIsEditing(false);
                  setNewNote({ timestamp: '0', title: '', content: '', chapterId: '' });
                }}
                size="sm"
              >
                취소
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Notes List */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h4>내 노트 ({notes.length})</h4>
          <Badge variant="secondary" className="text-xs">
            최근 순
          </Badge>
        </div>

        <ScrollArea className="h-[400px]">
          <div className="space-y-4">
            {sortedNotes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Edit3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>아직 작성된 노트가 없습니다.</p>
                <p className="text-sm">중요한 내용을 기록해보세요!</p>
              </div>
            ) : (
              sortedNotes.map((note, index) => (
                <div key={note.id}>
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatTime(note.timestamp)}
                          </Badge>
                          {note.chapterId && (
                            <Badge variant="secondary" className="text-xs">
                              {getChapterTitle(note.chapterId)}
                            </Badge>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {new Date(note.createdAt).toLocaleDateString('ko-KR', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <h5 className="font-medium">{note.title}</h5>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDeleteNote(note.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {note.content}
                    </p>
                    
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full"
                      onClick={() => onJumpToTime(Number(note.timestamp) || 0)}
                    >
                      <Play className="w-4 h-4 mr-1" />
                      {formatTime(note.timestamp)} 구간으로 이동
                    </Button>
                  </div>
                  
                  {index < sortedNotes.length - 1 && <Separator className="mt-4" />}
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </Card>
    </div>
  );
}
